import { useState, useEffect, useCallback, useRef } from 'react'
import {
  MessageSquare, Mic, Pencil, Save, RotateCcw, History, ChevronDown,
  RefreshCw, Wrench, Eye, Sparkles, AlertTriangle, Info,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api'
import PromptRefineDrawer from './PromptRefineDrawer'

// ── Types ────────────────────────────────────────────────────────────────

interface VoiceAssistantSummary {
  id: string
  name: string
  systemPrompt: string
  customBasePrompt: string | null
  assistantType: string
  isCustom: boolean
  voiceId: string | null
  updatedAt: string
}

// Voices available to admins per-agent. Mirrors VOICE_OPTIONS in
// VoiceEngineSection.tsx and TELNYX_VOICE_ID_MAP in telnyxDefaults.ts —
// keep all three in sync when adding a voice.
const PROMPT_STUDIO_VOICES = [
  { id: 'molly', name: 'Molly', description: 'Warm, professional (default)' },
  { id: 'lauren', name: 'Lauren', description: 'Friendly, comforting' },
  { id: 'nora', name: 'Nora', description: 'Calm, clear, natural' },
  { id: 'quinn', name: 'Quinn', description: 'Calm, authoritative (Telnyx)' },
]

interface PromptData {
  chatPrompt: { prompt: string | null; isCustom: boolean }
  voiceAssistants: VoiceAssistantSummary[]
  editHistory: Array<{
    id: string
    promptType: string
    voiceAssistantId: string | null
    previousPrompt: string | null
    newPrompt: string
    note: string | null
    editedByEmail: string
    editedByName: string
    createdAt: string
  }>
}

interface PromptPreview {
  base: string
  isCustomBase: boolean
  masterBase: string
  addendums: Array<{ tool: string; text: string }>
  final: string
  /** Slice A: drift indicator. 'failed' = DB ahead of Telnyx; 'synced' = both match. */
  syncStatus?: 'synced' | 'failed' | null
  syncError?: string | null
  /** Slice E: phone numbers currently routing to this agent. Empty = orphan, edits won't hit live calls. */
  routedPhoneNumbers?: Array<{ phoneNumber: string; role: 'inbound' | 'outbound' }>
  /** Slice G: true when a frozen signup snapshot exists for this agent. Drives the "Revert to original" button. */
  hasOriginal?: boolean
}

const TOOL_LABELS: Record<string, string> = {
  scheduling: 'Scheduling',
  transfer: 'Live Transfer',
  knowledge_base: 'Knowledge Base',
  sms: 'SMS',
  custom_webhooks: 'Custom Webhooks',
  agent_behavior: 'Agent Behavior',
}

const TOOL_DESCRIPTIONS: Record<string, string> = {
  scheduling: 'Consultation booking flow — uses the connected Nylas calendar when available, otherwise Legal Lady Calendar',
  transfer: 'Blind transfer to a live attorney — uses transferCall',
  knowledge_base: 'Firm FAQs / KB lookup — uses search_knowledge_base',
  sms: 'Send text messages during the call — uses send_message',
  custom_webhooks: 'Org-defined webhook tools',
  agent_behavior: 'Always-on conversation quality and safety rules',
}

interface PromptsSectionProps {
  orgId: string
}

// ── Top-level component ──────────────────────────────────────────────────

export default function PromptsSection({ orgId }: PromptsSectionProps) {
  const { toast } = useToast()
  const [promptData, setPromptData] = useState<PromptData | null>(null)
  const [isOpen, setIsOpen] = useState(true)
  const [activeTab, setActiveTab] = useState<string>('')
  const [refreshingAll, setRefreshingAll] = useState(false)

  const loadPrompts = useCallback(async () => {
    try {
      const response = await api.get(`/api/platform/organizations/${orgId}/prompts`)
      if (response.data.success) {
        setPromptData(response.data)
        if (!activeTab) {
          // Default to first voice assistant if any, otherwise chat
          const first = response.data.voiceAssistants[0]
          setActiveTab(first ? `voice:${first.id}` : 'chat')
        }
      }
    } catch (error) {
      console.error('Failed to load prompts:', error)
    }
  }, [orgId, activeTab])

  useEffect(() => { loadPrompts() }, [loadPrompts])

  const refreshAllAgents = async () => {
    if (!confirm(
      'Re-push every active Telnyx agent for this org? ' +
      'This refreshes the dynamic-variables webhook URL and other static fields ' +
      'so older agents catch up to current code defaults. No prompts or tools change.'
    )) return
    setRefreshingAll(true)
    try {
      const response = await api.post(`/api/platform/organizations/${orgId}/voice-agents/refresh`)
      const { refreshed, failed, results } = response.data
      if (failed === 0) {
        toast({ title: `Refreshed ${refreshed} agent${refreshed === 1 ? '' : 's'}` })
      } else {
        const failures = results.filter((r: any) => r.status === 'failed')
        toast({
          title: `Refreshed ${refreshed}, ${failed} failed`,
          description: failures.slice(0, 2).map((f: any) => `${f.name}: ${f.error}`).join('\n'),
          variant: 'destructive',
        })
      }
      loadPrompts()
    } catch (err: any) {
      toast({
        title: 'Refresh failed',
        description: err?.response?.data?.error || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setRefreshingAll(false)
    }
  }

  if (!promptData) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <RefreshCw className="h-5 w-5 mx-auto mb-2 animate-spin opacity-40" />
          Loading prompts...
        </CardContent>
      </Card>
    )
  }

  const customCount = (promptData.chatPrompt.isCustom ? 1 : 0)
    + promptData.voiceAssistants.filter(a => a.isCustom).length

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4" />
                Prompts
                {customCount > 0 && (
                  <Badge variant="outline" className="bg-[#ede9fe] dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 border-violet-200 dark:border-violet-700/50 text-[10px]">
                    {customCount} custom
                  </Badge>
                )}
              </CardTitle>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
            <CardDescription>Base prompts are admin-editable. Tool instructions are auto-injected from agent config.</CardDescription>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="flex justify-end mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={refreshAllAgents}
                disabled={refreshingAll || promptData.voiceAssistants.length === 0}
                title="Re-pushes every Telnyx agent's static fields (webhook URL, dynamic vars, post-call settings). Useful after a deploy URL change or to fix bulk drift."
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${refreshingAll ? 'animate-spin' : ''}`} />
                {refreshingAll ? 'Refreshing…' : 'Refresh all agents'}
              </Button>
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${promptData.voiceAssistants.length + 2}, minmax(0, 1fr))` }}>
                <TabsTrigger value="chat" className="text-xs gap-1.5">
                  <MessageSquare className="h-3 w-3" /> Chat
                  {promptData.chatPrompt.isCustom && <Dot />}
                </TabsTrigger>
                {promptData.voiceAssistants.map(a => (
                  <TabsTrigger key={a.id} value={`voice:${a.id}`} className="text-xs gap-1.5">
                    <Mic className="h-3 w-3" />
                    {a.assistantType === 'outbound' ? 'Outbound' : 'Inbound'}
                    {a.isCustom && <Dot />}
                  </TabsTrigger>
                ))}
                <TabsTrigger value="history" className="text-xs gap-1.5">
                  <History className="h-3 w-3" /> History
                  <span className="text-muted-foreground">({promptData.editHistory.length})</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="chat" className="mt-4">
                <ChatPromptPanel
                  orgId={orgId}
                  current={promptData.chatPrompt}
                  onChange={loadPrompts}
                />
              </TabsContent>

              {promptData.voiceAssistants.map(a => (
                <TabsContent key={a.id} value={`voice:${a.id}`} className="mt-4">
                  <VoicePromptPanel
                    orgId={orgId}
                    assistant={a}
                    onChange={loadPrompts}
                  />
                </TabsContent>
              ))}

              <TabsContent value="history" className="mt-4">
                <HistoryPanel
                  orgId={orgId}
                  history={promptData.editHistory}
                  onChange={loadPrompts}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}

function Dot() {
  return <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
}

// ── Chat prompt panel ────────────────────────────────────────────────────

function ChatPromptPanel({
  orgId,
  current,
  onChange,
}: {
  orgId: string
  current: { prompt: string | null; isCustom: boolean }
  onChange: () => void
}) {
  const { toast } = useToast()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)

  const startEdit = () => {
    setDraft(current.prompt || '')
    setNote('')
    setEditing(true)
  }

  const save = async () => {
    if (!draft.trim()) return
    setSaving(true)
    try {
      await api.put(`/api/platform/organizations/${orgId}/prompts/chat`, {
        prompt: draft,
        note: note || undefined,
      })
      toast({ title: 'Chat prompt saved' })
      setEditing(false)
      onChange()
    } catch {
      toast({ title: 'Failed to save', variant: 'destructive' })
    } finally { setSaving(false) }
  }

  const revert = async () => {
    if (!confirm('Revert chat prompt to master?')) return
    try {
      await api.post(`/api/platform/organizations/${orgId}/prompts/chat/revert-to-master`)
      toast({ title: 'Reverted to master' })
      onChange()
    } catch {
      toast({ title: 'Failed to revert', variant: 'destructive' })
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h4 className="font-medium text-sm">Chat system prompt</h4>
          {current.isCustom && (
            <Badge variant="outline" className="bg-[#ede9fe] dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 border-violet-200 dark:border-violet-700/50 text-[10px]">
              Custom
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          {current.isCustom && !editing && (
            <Button variant="outline" size="sm" onClick={revert}>
              <RotateCcw className="h-3 w-3 mr-1" /> Revert to template
            </Button>
          )}
          {!editing && (
            <Button size="sm" onClick={startEdit}>
              <Pencil className="h-3 w-3 mr-1" /> Edit
            </Button>
          )}
        </div>
      </div>

      {editing ? (
        <div className="space-y-2">
          <Textarea
            value={draft}
            onChange={e => setDraft(e.target.value)}
            rows={14}
            className="font-mono text-xs"
            placeholder="Enter the chat system prompt..."
          />
          <Input
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Optional note"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving}>
              <Save className="h-3 w-3 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        <pre className="whitespace-pre-wrap text-xs bg-muted/50 rounded-lg p-3 max-h-[400px] overflow-y-auto border">
          {current.prompt || 'No custom chat prompt set — using auto-generated prompt.'}
        </pre>
      )}
    </div>
  )
}

// ── Voice prompt panel — the main feature ────────────────────────────────

function VoicePromptPanel({
  orgId,
  assistant,
  onChange,
}: {
  orgId: string
  assistant: VoiceAssistantSummary
  onChange: () => void
}) {
  const { toast } = useToast()
  const [preview, setPreview] = useState<PromptPreview | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(true)
  const [draft, setDraft] = useState('')
  const [savedBase, setSavedBase] = useState<string>('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [toggling, setToggling] = useState(false)
  const [resyncing, setResyncing] = useState(false)
  const [voiceUpdating, setVoiceUpdating] = useState(false)
  const [voiceId, setVoiceId] = useState<string>(assistant.voiceId || 'molly')
  const [toggleOn, setToggleOn] = useState(assistant.isCustom)
  const [hasEdited, setHasEdited] = useState(false)
  const [refineOpen, setRefineOpen] = useState(false)

  // Reset local state when switching assistants (tab change)
  useEffect(() => {
    setToggleOn(assistant.isCustom)
    setVoiceId(assistant.voiceId || 'molly')
    setNote('')
    setHasEdited(false)
    setLoadingPreview(true)
    const load = async () => {
      try {
        const res = await api.get(
          `/api/platform/organizations/${orgId}/prompts/voice/${assistant.id}/preview`
        )
        if (res.data.success) {
          const p: PromptPreview = {
            base: res.data.base,
            isCustomBase: res.data.isCustomBase,
            masterBase: res.data.masterBase,
            addendums: res.data.addendums || [],
            final: res.data.final,
            syncStatus: res.data.syncStatus ?? null,
            syncError: res.data.syncError ?? null,
            routedPhoneNumbers: res.data.routedPhoneNumbers ?? [],
          }
          setPreview(p)
          // Seed the editor: stored custom text if there is one (even when
          // toggle is off — admin needs to see what they'd re-enable to),
          // otherwise the master as a starting point.
          const seed = assistant.customBasePrompt || p.masterBase
          setDraft(seed)
          setSavedBase(assistant.customBasePrompt || '')
        }
      } catch {
        toast({ title: 'Failed to load prompt preview', variant: 'destructive' })
      } finally { setLoadingPreview(false) }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistant.id])

  // Live preview: debounced POST that merges draft with current addendums.
  // Refresh only fires when the toggle is ON (otherwise the editor is
  // read-only and preview is already the master).
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!toggleOn) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.post(
          `/api/platform/organizations/${orgId}/prompts/voice/${assistant.id}/preview`,
          { baseOverride: draft, customEnabledOverride: true }
        )
        if (res.data.success) {
          setPreview(p => p ? {
            ...p,
            base: res.data.base,
            isCustomBase: res.data.isCustomBase,
            addendums: res.data.addendums || [],
            final: res.data.final,
          } : p)
        }
      } catch {
        // Silent — transient preview errors shouldn't block typing
      }
    }, 400)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, toggleOn, orgId, assistant.id])

  // Only flag "unsaved" when the admin has actually typed something AND the
  // current draft differs from whatever's saved. Showing "unsaved" just for
  // seeding the editor with the master template would cry wolf.
  const hasUnsavedChanges = toggleOn && hasEdited && draft.trim() !== savedBase.trim()

  const onToggleChange = async (enabled: boolean) => {
    // Warn if flipping OFF would discard unsaved edits. Toggling itself is
    // non-destructive on the server (text stays in custom_base_prompt), but
    // in-flight UI edits that haven't been saved DO get cleared when the
    // editor re-seeds below.
    if (!enabled && hasUnsavedChanges) {
      if (!confirm('You have unsaved changes in the editor. Turning Custom Prompt OFF will discard them (saved custom text on the server is preserved). Continue?')) {
        return
      }
    }

    // Optimistic local update for snappiness — revert if the request fails.
    const previous = toggleOn
    setToggleOn(enabled)
    setToggling(true)
    try {
      await api.patch(
        `/api/platform/organizations/${orgId}/prompts/voice/${assistant.id}/toggle-custom`,
        { enabled }
      )
      toast({ title: `Custom prompt ${enabled ? 'enabled' : 'disabled'}` })
      // Re-fetch preview so the right-side pane matches the new effective state
      const res = await api.get(
        `/api/platform/organizations/${orgId}/prompts/voice/${assistant.id}/preview`
      )
      if (res.data.success) {
        const newPreview: PromptPreview = {
          base: res.data.base,
          isCustomBase: res.data.isCustomBase,
          masterBase: res.data.masterBase,
          addendums: res.data.addendums || [],
          final: res.data.final,
        }
        setPreview(newPreview)
        // Re-seed the editor so read-only view matches the preview pane
        // exactly. Saved custom text on the server is preserved either way.
        if (enabled) {
          setDraft(savedBase || newPreview.masterBase)
        } else {
          setDraft(newPreview.masterBase)
        }
        setHasEdited(false)
      }
      onChange()
    } catch {
      setToggleOn(previous)
      toast({ title: 'Failed to toggle custom prompt', variant: 'destructive' })
    } finally { setToggling(false) }
  }

  const save = async () => {
    if (!draft.trim()) return
    setSaving(true)
    try {
      const saveResp = await api.put(
        `/api/platform/organizations/${orgId}/prompts/voice/${assistant.id}`,
        { prompt: draft, note: note || undefined }
      )

      // Slice A — surface Telnyx sync drift. Backend now returns
      // syncStatus + syncError; if the PATCH to Telnyx failed, the DB has
      // the new prompt but the live agent is still on the old one. Show a
      // warning toast so the admin retries instead of a green success.
      const syncStatus = saveResp.data?.syncStatus
      const syncError = saveResp.data?.syncError
      if (syncStatus === 'failed') {
        toast({
          title: 'Saved locally — but Telnyx sync FAILED',
          description: syncError || 'The DB has your new prompt but Telnyx is still on the old one. Try saving again, or check Railway logs.',
          variant: 'destructive',
        })
      } else if (syncStatus === 'no_provider') {
        toast({
          title: 'Saved (no provider attached)',
          description: 'Prompt stored in DB. No Telnyx assistant ID — nothing pushed.',
        })
      } else {
        toast({ title: 'Base prompt saved', description: 'Pushed to Telnyx with current tool addendums.' })
      }

      setSavedBase(draft)
      setToggleOn(true)
      setHasEdited(false)
      setNote('')
      // Refresh preview so the saved base + current addendums land on the right
      const res = await api.get(
        `/api/platform/organizations/${orgId}/prompts/voice/${assistant.id}/preview`
      )
      if (res.data.success) {
        setPreview({
          base: res.data.base,
          isCustomBase: res.data.isCustomBase,
          masterBase: res.data.masterBase,
          addendums: res.data.addendums || [],
          final: res.data.final,
        })
      }
      onChange()
    } catch {
      toast({ title: 'Failed to save', variant: 'destructive' })
    } finally { setSaving(false) }
  }

  const revert = async () => {
    if (!confirm(
      'Revert to template? This CLEARS your custom base prompt and re-renders the vertical template. ' +
      'To keep your custom text but temporarily use the template, turn the Custom Prompt toggle OFF instead.'
    )) return
    try {
      await api.post(`/api/platform/organizations/${orgId}/prompts/voice/${assistant.id}/revert-to-master`)
      toast({ title: 'Reverted to master', description: 'Custom base cleared; regenerated from org config.' })
      onChange()
    } catch {
      toast({ title: 'Failed to revert', variant: 'destructive' })
    }
  }

  const revertToOriginal = async () => {
    if (!confirm(
      'Revert to the original signup snapshot? This restores the prompt to what it was when ' +
      'this agent was first created — undoing all edits and KB personalisation. ' +
      'Your current prompt is logged in history and can be restored from there if needed.'
    )) return
    try {
      const response = await api.post(`/api/platform/organizations/${orgId}/prompts/voice/${assistant.id}/revert-to-original`)
      const { syncStatus, syncError } = response.data
      if (syncStatus === 'failed') {
        toast({
          title: 'Reverted, but Telnyx sync failed',
          description: syncError || 'The DB was reverted but the agent on Telnyx still has the old prompt.',
          variant: 'destructive',
        })
      } else {
        toast({ title: 'Reverted to original snapshot' })
      }
      onChange()
    } catch (err: any) {
      toast({
        title: 'Failed to revert',
        description: err?.response?.data?.error || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  const changeVoice = async (next: string) => {
    if (next === voiceId) return
    const previous = voiceId
    setVoiceId(next) // optimistic
    setVoiceUpdating(true)
    try {
      const res = await api.post(
        `/api/platform/organizations/${orgId}/voice-agents/${assistant.id}/voice`,
        { voiceId: next },
      )
      const { syncStatus, syncError } = res.data
      if (syncStatus === 'failed') {
        toast({
          title: 'Voice saved, Telnyx push failed',
          description: syncError || 'DB updated; the live agent is still on the old voice.',
          variant: 'destructive',
        })
      } else {
        toast({ title: `Voice → ${next}` })
      }
      onChange()
    } catch (err: any) {
      setVoiceId(previous) // rollback
      toast({
        title: 'Voice update failed',
        description: err?.response?.data?.error || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setVoiceUpdating(false)
    }
  }

  const resyncPrompt = async () => {
    setResyncing(true)
    try {
      const response = await api.post(
        `/api/platform/organizations/${orgId}/voice-agents/${assistant.id}/resync-prompt`,
      )
      const { syncStatus, syncError, promptLength } = response.data
      if (syncStatus === 'failed') {
        toast({
          title: 'Resync failed',
          description: syncError || 'DB updated but Telnyx push failed.',
          variant: 'destructive',
        })
      } else {
        toast({ title: `Pushed to Telnyx (${promptLength} chars)` })
      }
      onChange()
    } catch (err: any) {
      toast({
        title: 'Resync failed',
        description: err?.response?.data?.error || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setResyncing(false)
    }
  }

  const regenerateFromVertical = async () => {
    if (!confirm(
      'Regenerate this prompt from the current vertical template?\n\n' +
      'This OVERWRITES the prompt with a freshly-rendered template, sets it as the new "original" snapshot, ' +
      'and CLEARS this agent\'s saved KB answers (they were authored against the previous template).\n\n' +
      'The current prompt is logged in History and can be restored from there. Continue?'
    )) return
    try {
      const response = await api.post(`/api/platform/organizations/${orgId}/voice-agents/${assistant.id}/regenerate-vertical`)
      const { vertical, direction, syncStatus, syncError, charCount } = response.data
      const sizeNote = ` (${vertical}/${direction}, ${charCount} chars)`
      if (syncStatus === 'failed') {
        toast({
          title: `Regenerated, but Telnyx sync failed${sizeNote}`,
          description: syncError || 'DB updated; agent on Telnyx still has the old prompt.',
          variant: 'destructive',
        })
      } else {
        toast({ title: `Regenerated from template${sizeNote}` })
      }
      onChange()
    } catch (err: any) {
      toast({
        title: 'Regenerate failed',
        description: err?.response?.data?.error || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  const seedFromMaster = () => {
    if (preview?.masterBase && confirm('Replace the current draft with the vertical template as a starting point?')) {
      setDraft(preview.masterBase)
      setHasEdited(true)
    }
  }

  if (loadingPreview) {
    return (
      <div className="py-8 text-center text-muted-foreground text-sm">
        <RefreshCw className="h-4 w-4 mx-auto mb-2 animate-spin opacity-40" />
        Loading…
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Header strip: assistant name, toggle, actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 border rounded-lg p-3 bg-muted/20">
        <div className="flex items-center gap-3">
          <div>
            <div className="font-medium text-sm flex items-center gap-2">
              <Mic className="h-3.5 w-3.5 text-muted-foreground" />
              {assistant.name || 'Voice Assistant'}
              <Badge variant="secondary" className="text-[10px] capitalize">{assistant.assistantType}</Badge>
              {/* Slice A — sync drift pill. Visible only when the DB and
                  Telnyx have actually drifted apart. Hover for the error. */}
              {preview?.syncStatus === 'failed' && (
                <Badge
                  variant="destructive"
                  className="text-[10px] cursor-help"
                  title={preview.syncError || 'Last save failed to push to Telnyx. Try saving again.'}
                >
                  ⚠ Telnyx out of sync
                </Badge>
              )}
              {preview?.syncStatus === 'synced' && (
                <Badge variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                  ✓ Synced
                </Badge>
              )}
              {/* Slice E — orphan warning. If no phones route here, edits
                  won't affect live calls. Common cause: onboarding re-run
                  created a new agent but the phone still points at the old. */}
              {preview && preview.routedPhoneNumbers !== undefined && preview.routedPhoneNumbers.length === 0 && (
                <Badge
                  variant="destructive"
                  className="text-[10px] cursor-help"
                  title="No phone numbers route to this agent. Edits made here will NOT affect live calls. Check the Voice Agents Audit (super-admin → org settings) to find the canonical agent for this org and reassign the phone, or archive this orphan."
                >
                  ⚠ Orphan — no phones route here
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {toggleOn
                ? <>Using <strong className="text-violet-600 dark:text-violet-400">custom</strong> base. Edits replace the master on every call.</>
                : <>Using the <strong>master</strong> base auto-generated from org config.</>}
            </p>
            {/* Slice E — show which phone numbers route here so the admin
                has visual confirmation that edits will reach live calls. */}
            {preview?.routedPhoneNumbers && preview.routedPhoneNumbers.length > 0 && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Routes to: {preview.routedPhoneNumbers.map(p => `${p.phoneNumber} (${p.role})`).join(', ')}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Per-agent voice picker. Different from the org-level voice on
              VoiceEngineSection — this lets each agent (inbound vs outbound)
              use a different voice. Selecting here PATCHes the row's voice_id
              and pushes the new voice to the live Telnyx agent. */}
          <label className="flex items-center gap-2 select-none">
            <span className="text-xs font-medium whitespace-nowrap">Voice</span>
            <Select
              value={voiceId}
              onValueChange={changeVoice}
              disabled={voiceUpdating || toggling || saving}
            >
              <SelectTrigger className="h-7 text-xs w-[110px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROMPT_STUDIO_VOICES.map(v => (
                  <SelectItem key={v.id} value={v.id} className="text-xs">
                    <span className="font-medium">{v.name}</span>
                    <span className="ml-1.5 text-[10px] text-muted-foreground">— {v.description}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <span className="text-xs font-medium">Custom Prompt</span>
            <Switch
              checked={toggleOn}
              onCheckedChange={onToggleChange}
              disabled={toggling}
            />
            <span className={`text-[10px] font-medium uppercase tracking-wide ${toggleOn ? 'text-violet-600 dark:text-violet-400' : 'text-muted-foreground'}`}>
              {toggleOn ? 'On' : 'Off'}
            </span>
          </label>
          {savedBase && (
            <Button variant="outline" size="sm" onClick={revert} disabled={toggling || saving}>
              <RotateCcw className="h-3 w-3 mr-1" /> Clear custom
            </Button>
          )}
          {/* Slice G — Revert to original signup snapshot. Available when an
              original exists (agents created via the new vertical-template flow,
              or backfilled at migration time). Different from "Clear custom"
              which goes to the master path. */}
          {preview?.hasOriginal && (
            <Button variant="outline" size="sm" onClick={revertToOriginal} disabled={toggling || saving}
              title="Restore the prompt to the snapshot taken at signup, undoing all edits and personalisation.">
              <RotateCcw className="h-3 w-3 mr-1" /> Revert to original
            </Button>
          )}
          {/* Non-destructive resync: rebuild final prompt with current tool
              addendums + banned-phrases filter and push to Telnyx. Does NOT
              change custom_base_prompt — use this when the master template
              improved or to retry a failed sync. Differs from "Refresh agents"
              (which skips prompts entirely) and "Regenerate from template"
              (which overwrites the custom prompt). */}
          <Button
            variant="outline"
            size="sm"
            onClick={resyncPrompt}
            disabled={toggling || saving || resyncing}
            title="Rebuild this prompt with current tool addendums + banned-phrases filter and push to Telnyx. Non-destructive — your custom prompt is preserved."
          >
            <RefreshCw className={`h-3 w-3 mr-1 ${resyncing ? 'animate-spin' : ''}`} /> Push to Telnyx
          </Button>
          {/* Migration tool — regenerate from the current vertical template.
              Useful when an agent was created before the bake-at-signup
              architecture or when the master template has materially improved. */}
          <Button
            variant="outline"
            size="sm"
            onClick={regenerateFromVertical}
            disabled={toggling || saving || resyncing}
            title="Re-render the prompt from the current vertical template. Overwrites the existing prompt and clears KB answers; previous prompt is logged in History."
          >
            <Sparkles className="h-3 w-3 mr-1" /> Regenerate from template
          </Button>
          {/* AI-assisted refinement. Reads the current saved custom base +
              last 2 real call transcripts and asks Claude to revise per the
              admin's instruction, with placeholders frozen behind opaque
              sentinels. Disabled until there's a saved custom base — refining
              system_prompt would double-apply tool addendums on the next render. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRefineOpen(true)}
            disabled={toggling || saving || resyncing || !savedBase}
            title={savedBase
              ? 'Use AI + recent call transcripts to suggest a refined prompt. Placeholders stay frozen.'
              : 'Save a custom base prompt first (or click Regenerate from template) before refining with AI.'}
          >
            <Sparkles className="h-3 w-3 mr-1" /> Refine with AI
          </Button>
        </div>
      </div>

      <PromptRefineDrawer
        open={refineOpen}
        onClose={() => setRefineOpen(false)}
        orgId={orgId}
        assistantId={assistant.id}
        assistantName={assistant.name}
        currentPrompt={savedBase}
        onApplied={onChange}
      />

      {/* Slice H: Q&A personalization. Collapsed by default — admin opens
          it when they want to update answers and re-personalize. */}
      <KbQuestionsPanel
        orgId={orgId}
        assistantId={assistant.id}
        onPersonalize={onChange}
      />

      {/* Hint banners */}
      {!toggleOn && (
        <div className="flex items-start gap-2 text-xs text-muted-foreground border-l-2 border-muted-foreground/30 pl-3 py-1">
          <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <div>
            Custom prompt is OFF — the assistant is using the vertical template below.
            {savedBase ? ' Your saved custom text is preserved; flip the toggle back on to use it.' : ' Turn the toggle on to start writing your own base prompt.'}
          </div>
        </div>
      )}
      {toggleOn && !savedBase && (
        <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400 border-l-2 border-amber-400 pl-3 py-1">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <div>
            Custom prompt is ON but nothing is saved yet. The assistant is still using the template until you click <strong>Save</strong>.
            The editor below is pre-seeded with the template — edit and save to make it yours.
          </div>
        </div>
      )}

      {/* Split view: editor left, live preview right */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Editor pane */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1.5">
                <Pencil className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-medium">Base prompt</span>
                <span className="text-[11px] text-muted-foreground">— tone, questions, flow</span>
              </div>
            </div>
            {toggleOn && preview?.masterBase && (
              <Button variant="ghost" size="sm" onClick={seedFromMaster} className="text-[11px] h-6 px-2">
                <Sparkles className="h-3 w-3 mr-1" />
                Reset to template
              </Button>
            )}
          </div>

          <Textarea
            value={draft}
            onChange={e => {
              setDraft(e.target.value)
              setHasEdited(true)
            }}
            rows={22}
            readOnly={!toggleOn}
            className={`font-mono text-[11px] leading-relaxed ${!toggleOn ? 'bg-muted/40 cursor-not-allowed' : ''}`}
            placeholder="Write the base prompt — tone, qualifying questions, call flow. Do not include tool instructions; they're auto-injected."
          />

          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
            <span>{draft.length.toLocaleString()} chars · base only</span>
            {toggleOn && hasUnsavedChanges && <span className="text-amber-600 dark:text-amber-400 font-medium">● Unsaved changes</span>}
          </div>

          {toggleOn && (
            <div className="space-y-2 pt-1">
              <Input
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Optional note (e.g. 'Adjusted intake questions for DUI cases')"
                className="text-xs"
              />
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDraft(savedBase || preview?.masterBase || '')
                    setHasEdited(false)
                  }}
                  disabled={saving || !hasUnsavedChanges}
                >
                  Discard changes
                </Button>
                <Button size="sm" onClick={save} disabled={saving || !draft.trim() || !hasUnsavedChanges}>
                  <Save className="h-3 w-3 mr-1" />
                  {saving ? 'Saving…' : 'Save & push'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Preview pane */}
        <LivePreviewPane preview={preview} draft={toggleOn ? draft : undefined} />
      </div>

      {/* Active tools row */}
      {preview && <ToolChipsRow addendums={preview.addendums} />}
    </div>
  )
}

// ── Live preview pane ────────────────────────────────────────────────────

function LivePreviewPane({
  preview,
  draft,
}: {
  preview: PromptPreview | null
  draft?: string // when defined, the editor is editable and this is the live draft
}) {
  const [showInjectedDetails, setShowInjectedDetails] = useState(false)
  if (!preview) return null

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Eye className="h-3 w-3 text-muted-foreground" />
          <span className="text-xs font-medium">Live preview</span>
          <span className="text-[11px] text-muted-foreground">— base prompt plus managed addendums</span>
        </div>
        {preview.addendums.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px]"
            onClick={() => setShowInjectedDetails(v => !v)}
          >
            {showInjectedDetails ? 'Hide injected text' : 'Show injected text'}
          </Button>
        )}
      </div>

      <div className="border rounded-lg bg-muted/20 p-3 max-h-[550px] overflow-y-auto text-[11px] font-mono leading-relaxed space-y-3">
        {/* Base section */}
        <div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            {preview.isCustomBase || draft !== undefined ? (
              <>
                <Pencil className="h-2.5 w-2.5" />
                <span className="text-violet-600 dark:text-violet-400 font-semibold">Custom base</span>
              </>
            ) : (
              <>
                <Sparkles className="h-2.5 w-2.5" />
                <span className="font-semibold">Master base</span>
              </>
            )}
          </div>
          <pre className="whitespace-pre-wrap">{draft !== undefined ? draft : preview.base}</pre>
        </div>

        {/* Addendums — each visually distinct */}
        {preview.addendums.map(a => (
          <div key={a.tool} id={`addendum-${a.tool}`} className="border-t border-dashed border-muted-foreground/30 pt-3">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              <Wrench className="h-2.5 w-2.5" />
              <span className="font-semibold">Auto-injected · {TOOL_LABELS[a.tool] || a.tool}</span>
            </div>
            {showInjectedDetails ? (
              <pre className="whitespace-pre-wrap text-muted-foreground/90">{a.text}</pre>
            ) : (
              <div className="rounded-md border bg-background/60 px-3 py-2 text-[11px] text-muted-foreground font-sans">
                Managed by agent settings. Hidden from the editable base prompt to avoid double-injecting tool instructions.
              </div>
            )}
          </div>
        ))}

        {preview.addendums.length === 0 && (
          <div className="border-t border-dashed border-muted-foreground/30 pt-3 text-[11px] text-muted-foreground italic">
            No tool addendums active. Enable tools (booking, transfer, KB, SMS) in agent config and they'll appear here.
          </div>
        )}
      </div>

      <div className="text-[11px] text-muted-foreground">
        {((draft !== undefined ? draft.length : preview.base.length)
          + preview.addendums.reduce((n, a) => n + a.text.length + 2, 0)).toLocaleString()} chars total
      </div>
    </div>
  )
}

// ── Slice H: KB Q&A panel ────────────────────────────────────────────────

interface KbQuestion {
  key: string
  label: string
  helpText?: string
  kind: 'text' | 'longtext' | 'select' | 'yesno'
  options?: string[]
  default?: string
  maxLength?: number
  section?: string
}

interface KbSection {
  key: string
  label: string
  questions: KbQuestion[]
}

function KbQuestionsPanel({
  orgId,
  assistantId,
  onPersonalize,
}: {
  orgId: string
  assistantId: string
  onPersonalize: () => void
}) {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [vertical, setVertical] = useState<string>('')
  const [practiceAreas, setPracticeAreas] = useState<string[]>([])
  const [sections, setSections] = useState<KbSection[]>([])
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [originalAnswers, setOriginalAnswers] = useState<Record<string, string>>({})

  const load = async () => {
    setLoading(true)
    try {
      const res = await api.get(
        `/api/platform/organizations/${orgId}/voice-agents/${assistantId}/kb-questions`,
      )
      if (res.data.success) {
        setVertical(res.data.vertical || '')
        setPracticeAreas(Array.isArray(res.data.practiceAreas) ? res.data.practiceAreas : [])
        const incomingSections: KbSection[] = Array.isArray(res.data.sections) ? res.data.sections : []
        // Fallback for old API shape: synthesize a single 'general' section.
        const sectionsToUse: KbSection[] = incomingSections.length > 0
          ? incomingSections
          : [{ key: 'general', label: 'General', questions: res.data.questions || [] }]
        setSections(sectionsToUse)
        const allQuestions = sectionsToUse.flatMap(s => s.questions)
        const next: Record<string, string> = {}
        const orig: Record<string, string> = {}
        for (const q of allQuestions) {
          const saved = res.data.answers?.[q.key]?.text
          next[q.key] = saved !== undefined && saved !== null ? saved : (q.default || '')
          orig[q.key] = saved !== undefined && saved !== null ? saved : ''
        }
        setDrafts(next)
        setOriginalAnswers(orig)
      }
    } catch {
      toast({ title: 'Failed to load KB questions', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && sections.length === 0) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assistantId])

  const dirty = Object.keys(drafts).some(k => (drafts[k] || '') !== (originalAnswers[k] || ''))
  const totalQuestions = sections.reduce((n, s) => n + s.questions.length, 0)

  const submit = async () => {
    if (!confirm(
      'Personalize the prompt using these answers? This rewrites the agent\'s prompt via GPT and pushes to Telnyx. Your current prompt is logged in history and can be restored from there.'
    )) return
    setSubmitting(true)
    try {
      const res = await api.post(
        `/api/platform/organizations/${orgId}/voice-agents/${assistantId}/kb-answers`,
        { answers: drafts },
      )
      const { syncStatus, syncError, personalization } = res.data
      const sizeNote = personalization
        ? ` (${personalization.charsIn} → ${personalization.charsOut} chars)`
        : ''
      if (syncStatus === 'failed') {
        toast({
          title: `Personalized, but Telnyx sync failed${sizeNote}`,
          description: syncError || 'DB updated; agent on Telnyx still has the old prompt.',
          variant: 'destructive',
        })
      } else {
        toast({ title: `Personalized${sizeNote}` })
      }
      setOriginalAnswers({ ...drafts })
      onPersonalize()
    } catch (err: any) {
      toast({
        title: 'Personalization failed',
        description: err?.response?.data?.error || err?.message || 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border rounded-lg bg-muted/10">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/30 rounded-t-lg transition-colors"
          >
            <div className="flex items-center gap-2 text-xs">
              <Sparkles className="h-3.5 w-3.5 text-violet-500" />
              <span className="font-medium">Quick personalization (Q&amp;A)</span>
              <span className="text-muted-foreground">— answer a handful of questions, GPT rewrites the prompt</span>
            </div>
            <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-3 pb-3 space-y-3 border-t pt-3">
            {loading && (
              <div className="py-4 text-center text-muted-foreground text-xs">
                <RefreshCw className="h-3.5 w-3.5 mx-auto mb-1 animate-spin opacity-40" /> Loading questions…
              </div>
            )}
            {!loading && totalQuestions === 0 && open && (
              <div className="py-4 text-center text-muted-foreground text-xs">No questions configured for this vertical.</div>
            )}
            {!loading && totalQuestions > 0 && (
              <>
                <p className="text-[11px] text-muted-foreground">
                  Vertical: <span className="font-mono">{vertical || 'unknown'}</span>
                  {practiceAreas.length > 0 && (
                    <> · Practice areas: <span className="font-mono">{practiceAreas.join(', ')}</span></>
                  )}
                  . Empty answers leave the template's default wording in place.
                </p>
                <div className="space-y-5">
                  {sections.map(section => (
                    <div key={section.key} className="space-y-3">
                      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">
                        <span>{section.label}</span>
                        <span className="text-muted-foreground/60 font-normal normal-case tracking-normal">
                          · {section.questions.length} {section.questions.length === 1 ? 'question' : 'questions'}
                        </span>
                      </div>
                      {section.questions.map(q => (
                        <div key={q.key} className="space-y-1">
                          <label className="text-xs font-medium block">{q.label}</label>
                          {q.helpText && <p className="text-[10px] text-muted-foreground">{q.helpText}</p>}
                          {q.kind === 'select' ? (
                            <select
                              value={drafts[q.key] ?? ''}
                              onChange={e => setDrafts(d => ({ ...d, [q.key]: e.target.value }))}
                              className="w-full rounded-md border bg-background px-2 py-1 text-xs"
                            >
                              <option value="">— choose —</option>
                              {(q.options || []).map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : q.kind === 'yesno' ? (
                            <select
                              value={drafts[q.key] ?? ''}
                              onChange={e => setDrafts(d => ({ ...d, [q.key]: e.target.value }))}
                              className="w-full rounded-md border bg-background px-2 py-1 text-xs"
                            >
                              <option value="">— choose —</option>
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          ) : q.kind === 'longtext' ? (
                            <Textarea
                              value={drafts[q.key] ?? ''}
                              onChange={e => setDrafts(d => ({ ...d, [q.key]: e.target.value }))}
                              rows={3}
                              maxLength={q.maxLength}
                              className="text-xs"
                              placeholder={q.default}
                            />
                          ) : (
                            <Input
                              value={drafts[q.key] ?? ''}
                              onChange={e => setDrafts(d => ({ ...d, [q.key]: e.target.value }))}
                              maxLength={q.maxLength}
                              className="text-xs h-8"
                              placeholder={q.default}
                            />
                          )}
                          {q.maxLength && (drafts[q.key]?.length ?? 0) > q.maxLength * 0.9 && (
                            <p className="text-[10px] text-muted-foreground text-right">
                              {drafts[q.key]?.length || 0} / {q.maxLength}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button
                    size="sm"
                    onClick={submit}
                    disabled={submitting || !dirty}
                    title={!dirty ? 'No changes to apply' : 'Save answers and personalize the prompt with GPT'}
                  >
                    <Sparkles className={`h-3 w-3 mr-1 ${submitting ? 'animate-pulse' : ''}`} />
                    {submitting ? 'Personalizing…' : 'Save & personalize'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

// ── Active tools chips row ───────────────────────────────────────────────

function ToolChipsRow({ addendums }: { addendums: PromptPreview['addendums'] }) {
  if (addendums.length === 0) {
    return (
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground border-t pt-3">
        <Wrench className="h-3 w-3" />
        <span>No tools active. Addendums appear here when tools are enabled in agent config.</span>
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] border-t pt-3">
      <Wrench className="h-3 w-3 text-muted-foreground" />
      <span className="text-muted-foreground mr-1">Active tool addendums:</span>
      {addendums.map(a => (
        <button
          key={a.tool}
          type="button"
          onClick={() => {
            document.getElementById(`addendum-${a.tool}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }}
          className="inline-flex items-center gap-1 rounded-full bg-muted hover:bg-muted/70 px-2 py-0.5 text-[10px] transition-colors"
          title={TOOL_DESCRIPTIONS[a.tool]}
        >
          {TOOL_LABELS[a.tool] || a.tool}
        </button>
      ))}
    </div>
  )
}

// ── History panel ────────────────────────────────────────────────────────

function HistoryPanel({
  orgId,
  history,
  onChange,
}: {
  orgId: string
  history: PromptData['editHistory']
  onChange: () => void
}) {
  const { toast } = useToast()
  const revertToVersion = async (logId: string) => {
    if (!confirm('Revert to this version?')) return
    try {
      await api.post(`/api/platform/organizations/${orgId}/prompts/revert/${logId}`)
      toast({ title: 'Reverted', description: 'Prompt restored.' })
      onChange()
    } catch {
      toast({ title: 'Failed to revert', variant: 'destructive' })
    }
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground">
        <History className="h-5 w-5 mx-auto mb-2 opacity-40" />
        No edits yet.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {history.map(entry => (
        <div key={entry.id} className="border rounded-lg p-3 space-y-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="outline" className="text-[10px]">
                {entry.promptType === 'chat' ? 'Chat' : 'Voice'}
              </Badge>
              <span className="text-muted-foreground">{entry.editedByName || entry.editedByEmail}</span>
              <span className="text-muted-foreground">{new Date(entry.createdAt).toLocaleString()}</span>
            </div>
            {entry.previousPrompt &&
              entry.previousPrompt !== '[REVERTED TO MASTER]' &&
              entry.previousPrompt !== '[REVERTED TO TEMPLATE]' &&
              entry.previousPrompt !== 'CUSTOM_ON' &&
              entry.previousPrompt !== 'CUSTOM_OFF' && (
                <Button variant="outline" size="sm" onClick={() => revertToVersion(entry.id)}>
                  <RotateCcw className="h-3 w-3 mr-1" /> Revert to this
                </Button>
              )}
          </div>
          {entry.note && <p className="text-xs text-muted-foreground italic">{entry.note}</p>}
          {entry.previousPrompt && entry.previousPrompt.length > 20 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                View previous
              </summary>
              <pre className="whitespace-pre-wrap mt-1 bg-muted/50 rounded p-2 max-h-[200px] overflow-y-auto">
                {entry.previousPrompt}
              </pre>
            </details>
          )}
        </div>
      ))}
    </div>
  )
}
